import {
  toWSDateString,
  useModalOldContext,
  WSButton,
  WSButtonProps,
  WSElement,
  WSElementColorsType,
  WSFlexBox,
  WSIcon,
  WSIconName,
  WSList,
  WSLoader,
  WSMenuItem,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  IPayableSchema,
  PayrollWorkflowStrategy
} from "@wingspanhq/payments/dist/interfaces";
import {
  Payable,
  PayableStatus,
  PayeeReviewStatus,
  PayerApprovalStatus,
  Payee,
  UpdatePayableWorkflowStatusRequestPayerApprovalStatus
} from "@wingspanhq/wingspan-api-sdk";
import React, { useCallback } from "react";
import { useHistory, useLocation } from "react-router";
import { Actions } from "../../../components/Actions";
import { queriesPayableRows, queriesPayables } from "@query";
import { useSession } from "@hooks/useSession";
import { selectorPayeeDisplayName } from "../../../shared/selectors/selectorPayeeDisplayName";
import { useUserId } from "../../../query/hooks/helpers";
import {
  useMutationDownloadInvoicePdf,
  useMutationDownloadInvoiceReceiptPdf
} from "../../../query/payments/mutations";
import { useQueryPayrollSettings } from "../../../query/payments/queries";
import { QUERY_PAYABLES } from "../../../query/payables/keys";
import { useClientQuery } from "../../../query/users/queries";
import { WSQueries } from "../../../query/WSQuery";
import { selectorHasFundingMethod } from "../../../shared/selectors/selectorHasFundingMethod";
import { useModalAddDefaultPaymentMethodWarning } from "../../screens/AddDefaultPaymentMethodWarningModal";
import { getMatchingPayroll } from "../../utils/payables";
import { DELETE_PAYABLE_MODAL } from "../DeletePayableModal/DeletePayableModal";
import {
  InvoiceStatusTimeline,
  InvoiceTimelineStatus
} from "../InvoiceStatusTimeline/InvoiceStatusTimeline";
import { REJECT_PAYABLE_MODAL } from "../RejectPayableModal/RejectPayableModal";

const PAYABLE_LIST_DEPENDENCIES = [
  QUERY_PAYABLES,
  queriesPayableRows.searchPayableRows.queryKeyBase
];

type Props = { payable: Payable; payee: Payee };

export const PayableDetailsHeader: React.FC<Props> = ({ payable, payee }) => {
  const userId = useUserId();
  const { accountId } = useSession();
  const queryPayrollSettings = useQueryPayrollSettings(userId);
  const queryClient = useClientQuery(userId);

  const [updateWorkflowStatus, updateWorkflowStatusMeta] =
    queriesPayables.updatePayableWorkflowStatus.useMutation({
      dependencies: PAYABLE_LIST_DEPENDENCIES
    });
  const [openPayable, openPayableMeta] =
    queriesPayables.openPayable.useMutation({
      dependencies: PAYABLE_LIST_DEPENDENCIES
    });
  const { openModal } = useModalOldContext();
  const modalAddDefaultPaymentMethodWarning =
    useModalAddDefaultPaymentMethodWarning();
  const [downloadInvoicePdf] = useMutationDownloadInvoicePdf();
  const [downloadInvoiceReceiptPdf, downloadInvoiceReceiptPdfMeta] =
    useMutationDownloadInvoiceReceiptPdf();

  const history = useHistory();
  const location = useLocation();

  const payableId = payable.id!;

  const setPayerApprovalStatus = useCallback(
    (
      payerApprovalStatus: UpdatePayableWorkflowStatusRequestPayerApprovalStatus
    ) =>
      updateWorkflowStatus({
        payableId,
        updatePayableWorkflowStatusRequest: { payerApprovalStatus }
      }),
    [payableId, updateWorkflowStatus]
  );

  const onEdit = useCallback(() => {
    history.push({
      pathname: `/member/invoices/payables/${payableId}/edit`,
      state: {
        backPath: location.pathname + (location.search || "")
      }
    });
  }, [history, location.pathname, location.search, payableId]);

  return (
    <WSElement>
      <WSQueries
        queries={{
          queryPayrollSettings,
          queryClient
        }}
      >
        {({
          queryPayrollSettings: { data: payrollSettings },
          queryClient: { data: client }
        }) => {
          const status = payable.status;
          const payerApproval = payable.payerApprovalStatus;
          const isDualStage =
            payrollSettings.workflow === PayrollWorkflowStrategy.DualStage;
          const hasEditAccess = !!accountId && payable.accountId === accountId;

          const isOpen =
            status === PayableStatus.OPENED || status === PayableStatus.PENDING;
          const isOverdue =
            status === PayableStatus.OPENED &&
            !!payable.dueDate &&
            payable.dueDate < new Date();
          const isRejected =
            payerApproval === PayerApprovalStatus.DECLINED &&
            payable.payeeReviewStatus !== PayeeReviewStatus.RESUBMITTED;
          const isApprovalPending =
            isOpen &&
            payerApproval !== PayerApprovalStatus.APPROVED &&
            payerApproval !== PayerApprovalStatus.DECLINED;
          const isApproved =
            isOpen && payerApproval === PayerApprovalStatus.APPROVED;

          const openNowButtonProps = {
            children: "Open now",
            onClick: () => {
              openPayable({ payableId, ifMatch: "*" });
            },
            loading: openPayableMeta.isLoading
          };

          const preApproveNowButtonProps = {
            children: "Pre-Approve now",
            onClick: () => {
              setPayerApprovalStatus(
                UpdatePayableWorkflowStatusRequestPayerApprovalStatus.PRE_APPROVED
              );
            },
            loading: updateWorkflowStatusMeta.isLoading
          };

          const approveNowButtonProps = {
            children: "Approve now",
            onClick: () => {
              if (selectorHasFundingMethod(payrollSettings, client)) {
                setPayerApprovalStatus(
                  UpdatePayableWorkflowStatusRequestPayerApprovalStatus.APPROVED
                );
              } else {
                modalAddDefaultPaymentMethodWarning.open();
              }
            },
            loading: updateWorkflowStatusMeta.isLoading
          };

          const unapproveButtonProps = {
            children: "Unapprove",
            onClick: () => {
              setPayerApprovalStatus(
                isDualStage && payerApproval === PayerApprovalStatus.APPROVED
                  ? UpdatePayableWorkflowStatusRequestPayerApprovalStatus.PRE_APPROVED
                  : UpdatePayableWorkflowStatusRequestPayerApprovalStatus.PENDING
              );
            },
            loading: updateWorkflowStatusMeta.isLoading
          };

          const downloadPDFButtonProps = {
            children: "Download receipt PDF",
            onClick: () => {
              downloadInvoiceReceiptPdf({ invoiceId: payableId });
            },
            loading: downloadInvoiceReceiptPdfMeta.isLoading
          };

          const editButtonProps = {
            children: "Edit",
            onClick: onEdit
          };

          let icon: WSIconName | undefined;
          let iconColor: WSElementColorsType | undefined;
          let header: string = "";
          let subheader: string = "";
          let timelineStatus: InvoiceTimelineStatus | undefined;
          let buttonProps: WSButtonProps | null = null;

          if (isRejected) {
            header = "Invoice rejected";
            icon = "alert";
            iconColor = "red400";
            subheader = "Invoice was rejected";
            timelineStatus = InvoiceTimelineStatus.Rejected;
          } else if (isApprovalPending) {
            if (isDualStage) {
              if (payerApproval === PayerApprovalStatus.PRE_APPROVED) {
                subheader = "Approve or pay now";
                buttonProps = approveNowButtonProps;
                timelineStatus = InvoiceTimelineStatus.PreApproved;
              } else {
                subheader = "Pre-Approve";
                buttonProps = preApproveNowButtonProps;
                timelineStatus = InvoiceTimelineStatus.Open;
              }
            } else {
              subheader = "Approve or pay now";
              buttonProps = approveNowButtonProps;
              timelineStatus = InvoiceTimelineStatus.Open;
            }

            if (status === PayableStatus.PENDING) {
              header = "Eligibility pending";
              subheader = "Waiting on contractor to enable digital payments";
            } else if (isOverdue) {
              header = "Invoice payment is overdue";
              icon = "info-circle";
              iconColor = "red400";
            } else {
              header = "Invoice open";
            }
          } else if (isApproved) {
            const payroll = getMatchingPayroll(payrollSettings, {
              dueDate: payable.dueDate,
              client: { payDate: payable.scheduledPaymentDate ?? undefined }
            } as IPayableSchema);

            header = "Approved for payment";
            subheader = `Will be paid on ${
              payroll
                ? toWSDateString(payroll.date, "monthDate")
                : "future payroll"
            }`;
            buttonProps = unapproveButtonProps;
            timelineStatus = InvoiceTimelineStatus.Approved;
          } else if (status === PayableStatus.PAID) {
            header = "Invoice Paid";
            subheader = payable.events?.markedPaidAt
              ? `Invoice was marked as paid on ${toWSDateString(
                  payable.events.markedPaidAt,
                  "monthDate"
                )}`
              : payable.events?.paidAt
                ? `Invoice was processed (paid) on ${toWSDateString(
                    payable.events.paidAt,
                    "monthDate"
                  )}`
                : "Invoice was paid";
            buttonProps = downloadPDFButtonProps;
            timelineStatus = InvoiceTimelineStatus.Paid;
          } else if (status === PayableStatus.PAYMENT_IN_TRANSIT) {
            header = "Waiting on wire to arrive";
            subheader = "Invoice was marked paid via bank transfer";
          } else if (status === PayableStatus.CREATED) {
            header = "This is a draft";
            subheader = "Open invoice to pay";
            buttonProps =
              (payable.amount ?? 0) > 0 ? openNowButtonProps : editButtonProps;
          }

          const menuItems: WSMenuItem[] = [];

          if (
            payerApproval === PayerApprovalStatus.PRE_APPROVED ||
            payerApproval === PayerApprovalStatus.APPROVED
          ) {
            menuItems.push({
              icon: "block",
              label: "Unapprove",
              onClick: () => {
                setPayerApprovalStatus(
                  isDualStage && payerApproval === PayerApprovalStatus.APPROVED
                    ? UpdatePayableWorkflowStatusRequestPayerApprovalStatus.PRE_APPROVED
                    : UpdatePayableWorkflowStatusRequestPayerApprovalStatus.PENDING
                );
              }
            });
          }

          if (
            (status === PayableStatus.CREATED ||
              status === PayableStatus.OPENED ||
              status === PayableStatus.PENDING) &&
            hasEditAccess
          ) {
            menuItems.push({
              icon: "edit",
              label: "Edit",
              onClick: onEdit
            });
          }

          if (isApprovalPending && !isRejected) {
            menuItems.push({
              icon: "exit",
              label: "Reject",
              onClick: () => {
                openModal(REJECT_PAYABLE_MODAL, {
                  payee,
                  payable
                });
              }
            });
          }

          if (status === PayableStatus.CREATED && hasEditAccess) {
            menuItems.push({
              icon: "exit",
              label: "Remove",
              onClick: () => {
                openModal(DELETE_PAYABLE_MODAL, {
                  payable,
                  payee
                });
              }
            });
          }

          if (
            (status === PayableStatus.OPENED ||
              status === PayableStatus.PENDING) &&
            hasEditAccess
          ) {
            menuItems.push({
              icon: "exit",
              label: "Cancel invoice",
              onClick: () => {
                openModal(DELETE_PAYABLE_MODAL, {
                  payable,
                  payee
                });
              }
            });
          }

          // Download PDF
          if (
            status !== PayableStatus.CREATED &&
            status !== PayableStatus.PENDING
          ) {
            menuItems.push({
              icon: "download",
              label: "Download PDF",
              onClick: () => {
                downloadInvoicePdf({ invoiceId: payableId });
              }
            });
          }

          // Download Receipt PDF
          if (status === PayableStatus.PAID) {
            menuItems.push({
              icon: "download",
              label: "Download receipt PDF",
              onClick: () => {
                downloadInvoiceReceiptPdf({ invoiceId: payableId });
              }
            });
          }

          let timelineStatuses = [
            InvoiceTimelineStatus.Open,
            InvoiceTimelineStatus.Approved,
            InvoiceTimelineStatus.Paid,
            InvoiceTimelineStatus.Deposited
          ];
          if (isDualStage) {
            timelineStatuses = [
              InvoiceTimelineStatus.Open,
              InvoiceTimelineStatus.PreApproved,
              InvoiceTimelineStatus.Approved,
              InvoiceTimelineStatus.Paid,
              InvoiceTimelineStatus.Deposited
            ];
          }
          if (isRejected) {
            timelineStatuses = [
              InvoiceTimelineStatus.Open,
              InvoiceTimelineStatus.Rejected
            ];
          }

          function moveToCollaboratorDetails() {
            history.push(`/member/payees/${payable.payeeId}`);
          }

          return (
            <WSList gap="XL">
              <WSElement>
                <WSFlexBox.CenterY>
                  {icon && (
                    <WSIcon block name={icon} color={iconColor} mr="M" />
                  )}
                  <WSText.Heading5>{header}</WSText.Heading5>
                </WSFlexBox.CenterY>
                <WSText>{subheader}</WSText>
              </WSElement>

              <WSFlexBox gap="S">
                <WSButton.Link onClick={moveToCollaboratorDetails}>
                  {selectorPayeeDisplayName(payee)}
                </WSButton.Link>
                <WSButton.Link onClick={moveToCollaboratorDetails}>
                  {payee.email}
                </WSButton.Link>
              </WSFlexBox>

              <InvoiceStatusTimeline
                currentStatus={timelineStatus}
                statuses={timelineStatuses}
              />

              {(!!buttonProps || menuItems.length > 0) && (
                <WSFlexBox
                  wrap="nowrap"
                  alignItems="center"
                  fullWidth
                  justify="flex-end"
                >
                  {buttonProps ? (
                    <WSButton
                      fullWidth
                      {...buttonProps}
                      mr={menuItems.length > 0 ? "M" : undefined}
                    />
                  ) : "onClick" in menuItems[0] ? (
                    <WSButton
                      fullWidth
                      {...(menuItems[0] as WSButtonProps)}
                      kind="Secondary"
                      mr={menuItems.length > 0 ? "M" : undefined}
                    />
                  ) : null}
                  {updateWorkflowStatusMeta.isLoading ? (
                    <WSLoader.Spinner size="S" />
                  ) : (
                    menuItems.length > 0 && (
                      <Actions items={menuItems} name="payableActions" />
                    )
                  )}
                </WSFlexBox>
              )}
            </WSList>
          );
        }}
      </WSQueries>
    </WSElement>
  );
};
